/* eslint-disable no-unused-vars */
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  Search,
  Filter,
  ChevronDown,
  Download,
  Plus,
  Calendar,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { statusOptions, wageStatusOptions } from "../../../utils/constants";
import UpdatePaymentModal from "../../../components/invoice/UpdatePaymentModal";
import ViewDetailsModal from "../../../components/invoice/ViewDetailsModal";
import WaivePenaltyModal from "../../../components/invoice/WaivePenaltyModal";
import HistoryModal from "../../../components/invoice/HistoryModal";
import ManualPaymentsTable from "../../../components/invoice/ManualPaymentsTable";
import WagePaymentsTable from "../../../components/invoice/WagePaymentsTable";
import NoteModal from "../../../components/invoice/NoteModal";

const InvoicesList = () => {
  const navigate = useNavigate();
  const {
    manualPayments,
    fetchManualPayments,
    manualPaymentsPagination,
    wagePayments,
    fetchWagePayments,
    updateManualPayment,
    waivePenalty,
    getManualPaymentById,
  } = useContext(AdminContext);

  // State
  const [activeTab, setActiveTab] = useState("Manual");
  const [filteredManualPayments, setFilteredManualPayments] = useState([]);
  const [wagePaymentsList, setWagePaymentsList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [wageStatusFilter, setWageStatusFilter] = useState("All Payments"); // Changed to match manual payment style

  // Filtering & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [statusFilter, setStatusFilter] = useState("All Invoices");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Date Picker State
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const datePickerRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Modals
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showWaivePenaltyModal, setShowWaivePenaltyModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");
  const [error, setError] = useState("");
  const [penaltyNote, setPenaltyNote] = useState("");

  // Helper function to get initials
  const getInitials = (name) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Helper function to get random color based on name
  const getAvatarColor = (name) => {
    if (!name) return "#6B7280";
    const colors = [
      "#EF4444",
      "#F59E0B",
      "#10B981",
      "#3B82F6",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
      "#F97316",
      "#84CC16",
      "#6366F1",
    ];
    const index = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  // Fetch data
  useEffect(() => {
    if (activeTab === "Manual") {
      fetchManualPayments({
        status: selectedStatus,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
    } else {
      fetchWagePayments();
    }
  }, [
    activeTab,
    selectedStatus,
    currentPage,
    itemsPerPage,
    searchTerm,
    dateRange,
    fetchManualPayments,
    fetchWagePayments,
  ]);

  // Map manual payments to format
  useEffect(() => {
    if (manualPayments && Array.isArray(manualPayments)) {
      const formatted = manualPayments.map((p) => {
        const dueDate = new Date(p.dueDate);
        const today = new Date();
        const isOverdue = p.payingStatus !== "paid" && today > dueDate;
        const daysOverdue = isOverdue
          ? Math.floor((today - dueDate) / (1000 * 60 * 60 * 24))
          : 0;

        let status = "Not paid";
        if (p.payingStatus === "paid") {
          status = "Paid";
        } else if (p.payingStatus === "in progress") {
          status = "Pending";
        } else if (isOverdue) {
          status = "Overdue";
        }

        const amountToPay = Number(p.amountToPay || 0);
        let penaltyAmount = Number(p.penaltyAmount || 0);
        const totalPaid = Number(p.totalPaid || 0);

        if (isOverdue && penaltyAmount === 0 && !p.penaltyWaived) {
          penaltyAmount = (amountToPay - totalPaid) * 0.2;
        }

        const totalAmountDue = amountToPay + penaltyAmount;
        const remainingBalance = Math.max(0, totalAmountDue - totalPaid);

        return {
          id: p.id,
          _id: p.id,
          name: p.name || "N/A",
          client: {
            name: p.name || "N/A",
            phone: p.phone || "",
            address: p.address || "",
            initials: getInitials(p.name),
            avatarColor: getAvatarColor(p.name),
          },
          dueDate: p.dueDate,
          displayDueDate: dueDate.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          createdAtDate: p.createdAtDate || p.createdAt,
          displayCreatedDate: new Date(
            p.createdAtDate || p.createdAt,
          ).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          amount: amountToPay,
          amountToPay: amountToPay,
          penaltyAmount: penaltyAmount,
          totalAmountDue,
          totalPaid: totalPaid,
          remainingBalance,
          status,
          payingStatus: p.payingStatus,
          type: p.type?.charAt(0).toUpperCase() + p.type?.slice(1) || "Client",
          isOverdue,
          daysOverdue,
          penaltyWaived: p.penaltyWaived || false,
          updateHistory: p.history || [],
          isManual: true,
          createdBy: p.createdBy,
          description: p.description || "",
        };
      });
      setFilteredManualPayments(formatted);
    }
  }, [manualPayments]);

  // Map wage payments to format
  useEffect(() => {
    if (wagePayments && Array.isArray(wagePayments)) {
      setWagePaymentsList(
        wagePayments.map((p) => {
          return {
            id: p.id,
            _id: p.id,
            // Client/User data
            user: p.user || null,
            clientName: p.user?.name || "N/A",
            // Caregiver data
            caregiver: p.caregiver || null,
            caregiverName: p.caregiver?.name || "N/A",
            // Amount fields - use the correct field names from backend
            amount: parseFloat(p.grossAmount) || 0,
            grossAmount: parseFloat(p.grossAmount) || 0,
            platformFee: parseFloat(p.platformAmount ?? p.platformFee) || 0,
            platformAmount:
              parseFloat(p.platformAmount ?? p.platformFee) || 0,
            caregiverAmount: parseFloat(p.caregiverAmount) || 0,
            // Period field
            period: p.periodStart || null,
            month: p.periodStart || null,
            // Status
            status: p.status || "PENDING",
            // Dates
            createdAt: p.createdAt,
            paidAt: p.paidAt || null,
            // Contract reference
            contract: p.contract || null,
            // Transaction reference
            txRef: p.txRef || "",
            transactionRef: p.txRef || "",
            isManual: false,
          };
        }),
      );
    }
  }, [wagePayments]);

  // Close date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Filtering Logic ---
  const startDate = dateRange.start ? new Date(dateRange.start) : null;
  const endDate = dateRange.end ? new Date(dateRange.end) : null;
  if (endDate) {
    endDate.setHours(23, 59, 59, 999);
  }

  // Filter manual payments based on selected status filter
  const filteredManual = filteredManualPayments;

  const filteredSubs = wagePaymentsList.filter((p) => {
    // Search matches: client name, client email, caregiver name, transaction ref, or ID
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (p.user?.name || p.clientName || "")
        .toLowerCase()
        .includes(searchLower) ||
      (p.user?.email || "").toLowerCase().includes(searchLower) ||
      (p.caregiver?.name || p.caregiverName || "")
        .toLowerCase()
        .includes(searchLower) ||
      (p.transactionRef || "").toLowerCase().includes(searchLower) ||
      (p.id || "").toLowerCase().includes(searchLower);

    // Status filter for wage payments - map dropdown values to status
    let matchesStatus = true;
    if (wageStatusFilter === "Pending") {
      matchesStatus = p.status.toUpperCase() === "PENDING";
    } else if (wageStatusFilter === "Success") {
      matchesStatus = p.status.toUpperCase() === "SUCCESS";
    } else if (wageStatusFilter === "Failed") {
      matchesStatus = p.status.toUpperCase() === "FAILED";
    } else if (wageStatusFilter === "Cancelled") {
      matchesStatus = p.status.toUpperCase() === "CANCELLED";
    }
    // "All Payments" includes everything

    const paymentDate = new Date(p.createdAt || p.date);
    const matchesDate =
      (!startDate || paymentDate >= startDate) &&
      (!endDate || paymentDate <= endDate);

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Pagination
  const paginatedManual = filteredManualPayments;
  const totalPagesManual = manualPaymentsPagination.totalPages || 1;

  const paginatedSubs = filteredSubs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPagesSubs = Math.ceil(filteredSubs.length / itemsPerPage);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    activeTab,
    dateRange,
    wageStatusFilter,
    itemsPerPage,
  ]);

  // --- Handlers ---
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleFullPayment = async () => {
    if (selectedPayment) {
      const amountToPay = Number(selectedPayment.amountToPay || 0);
      const totalPaid = Number(selectedPayment.totalPaid || 0);
      const dueDate = new Date(selectedPayment.dueDate);
      const today = new Date();
      const isOverdue =
        selectedPayment.payingStatus !== "paid" && today > dueDate;

      let penaltyAmount = Number(selectedPayment.penaltyAmount || 0);
      if (isOverdue && penaltyAmount === 0 && !selectedPayment.penaltyWaived) {
        penaltyAmount = (amountToPay - totalPaid) * 0.2;
      }

      const totalAmountDue = amountToPay + penaltyAmount;
      const remaining = Math.max(0, totalAmountDue - totalPaid);

      const updatedData = {
        amountPaid: remaining,
        note: "Full payment completed",
      };

      try {
        await updateManualPayment(selectedPayment.id, updatedData);
        setShowUpdateModal(false);
        fetchManualPayments({
          status: selectedStatus,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          startDate: dateRange.start,
          endDate: dateRange.end,
        });
      } catch (error) {
        // Error is handled by context toast
      }
    }
  };

  const handlePartialPayment = async () => {
    const amount = parseFloat(partialAmount);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (selectedPayment) {
      const amountToPay = Number(selectedPayment.amountToPay || 0);
      const totalPaid = Number(selectedPayment.totalPaid || 0);
      const dueDate = new Date(selectedPayment.dueDate);
      const today = new Date();
      const isOverdue =
        selectedPayment.payingStatus !== "paid" && today > dueDate;

      let penaltyAmount = Number(selectedPayment.penaltyAmount || 0);
      if (isOverdue && penaltyAmount === 0 && !selectedPayment.penaltyWaived) {
        penaltyAmount = (amountToPay - totalPaid) * 0.2;
      }

      const totalAmountDue = amountToPay + penaltyAmount;
      const remainingBalance = Math.max(0, totalAmountDue - totalPaid);

      if (amount > remainingBalance) {
        setError("Amount cannot exceed remaining balance");
        return;
      }

      const updatedData = {
        amountPaid: amount,
        note: "Partial payment",
      };

      try {
        await updateManualPayment(selectedPayment.id, updatedData);
        setShowUpdateModal(false);
        setPartialAmount("");
        setError("");
        fetchManualPayments({
          status: selectedStatus,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          startDate: dateRange.start,
          endDate: dateRange.end,
        });
      } catch (error) {
        // Error is handled by context toast
      }
    }
  };

  const handleWaivePenalty = async () => {
    if (selectedPayment && penaltyNote.trim()) {
      try {
        await waivePenalty(selectedPayment.id, penaltyNote);
        setShowWaivePenaltyModal(false);
        setPenaltyNote("");
        fetchManualPayments({
          status: selectedStatus,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          startDate: dateRange.start,
          endDate: dateRange.end,
        });
      } catch (error) {
        // Error is handled by context toast
      }
    } else {
      setError("Please enter a reason for waiving the penalty");
    }
  };

  const getStatusBadge = useCallback((status) => {
    const styles = {
      Paid: "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white",
      Success: "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white",
      "Not paid": "bg-red-600 text-white dark:bg-red-700 dark:text-white",
      Failed: "bg-red-600 text-white dark:bg-red-700 dark:text-white",
      Pending: "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white",
      Overdue: "bg-orange-600 text-white dark:bg-orange-700 dark:text-white",
      pending: "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white",
      success: "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white",
      cancelled: "bg-gray-600 text-white dark:bg-gray-700 dark:text-white",
      Draft: "bg-gray-600 text-white dark:bg-gray-700 dark:text-white",
      Cancelled: "bg-gray-600 text-white dark:bg-gray-700 dark:text-white",
    };

    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-bold ${
          styles[status] || styles["Draft"]
        }`}
      >
        {typeof status === "string"
          ? status.charAt(0).toUpperCase() + status.slice(1)
          : status}
      </span>
    );
  }, []);

  const applyDateFilter = () => {
    setShowDatePicker(false);
    setCurrentPage(1);
  };

  // Export functionality
  const handleExport = () => {
    if (activeTab === "Manual") {
      exportManualPayments();
    } else {
      exportWagePayments();
    }
  };

  const exportManualPayments = () => {
    try {
      // Get filtered data
      const dataToExport = filteredManual;

      if (dataToExport.length === 0) {
        toast.warning("No data to export");
        return;
      }

      // Prepare CSV headers
      const headers = [
        "ID",
        "Client Name",
        "Phone",
        "Address",
        "Type",
        "Amount Due (Br)",
        "Penalty Amount (Br)",
        "Total Amount Due (Br)",
        "Total Paid (Br)",
        "Remaining Balance (Br)",
        "Status",
        "Due Date",
        "Created Date",
        "Days Overdue",
        "Penalty Waived",
        "Description",
      ];

      // Prepare CSV rows
      const rows = dataToExport.map((payment) => [
        payment.id || "",
        payment.client.name || "",
        payment.client.phone || "",
        payment.client.address || "",
        payment.type || "",
        payment.amount.toFixed(2),
        payment.penaltyAmount.toFixed(2),
        payment.totalAmountDue.toFixed(2),
        payment.totalPaid.toFixed(2),
        payment.remainingBalance.toFixed(2),
        payment.status || "",
        payment.displayDueDate || "",
        payment.displayCreatedDate || "",
        payment.daysOverdue || 0,
        payment.penaltyWaived ? "Yes" : "No",
        payment.description || "",
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `manual_payments_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${dataToExport.length} manual payments`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export manual payments");
    }
  };

  const exportWagePayments = () => {
    try {
      // Get filtered data
      const dataToExport = filteredSubs;

      if (dataToExport.length === 0) {
        toast.warning("No data to export");
        return;
      }

      // Prepare CSV headers
      const headers = [
        "ID",
        "Client Name",
        "Client Email",
        "Caregiver Name",
        "Caregiver Email",
        "Gross Amount (Br)",
        "Platform Share 20% of Net (Br)",
        "Caregiver Amount 80% of Net (Br)",
        "Status",
        "Period",
        "Payment Date",
        "Transaction Reference",
      ];

      // Prepare CSV rows
      const rows = dataToExport.map((payment) => [
        payment.id || "",
        payment.user?.name || payment.clientName || "",
        payment.user?.email || "",
        payment.caregiver?.name || payment.caregiverName || "",
        payment.caregiver?.email || "",
        payment.grossAmount?.toFixed(2) || payment.amount?.toFixed(2) || "0.00",
        payment.platformAmount?.toFixed(2) ||
          payment.platformFee?.toFixed(2) ||
          "0.00",
        payment.caregiverAmount?.toFixed(2) || "0.00",
        payment.status || "",
        payment.period
          ? new Date(payment.period).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
            })
          : "",
        (payment.paidAt || payment.createdAt)
          ? new Date(payment.paidAt || payment.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "",
        payment.transactionRef || "",
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `wage_payments_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${dataToExport.length} wage payments`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export wage payments");
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm("");
    setSearchInput("");
    setStatusFilter("All Invoices");
    setWageStatusFilter("All Payments");
    setCurrentPage(1);
  };

  // Handle status filter selection
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
  };

  // Table action handlers
  const handleWaivePenaltyClick = async (payment) => {
    setSelectedPayment(payment);
    setShowWaivePenaltyModal(true);
    setPenaltyNote("");
    setError("");
  };

  const handleViewDetailsClick = (payment) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  const handleViewNotesClick = (payment) => {
    setSelectedPayment(payment);
    setShowNoteModal(true);
  };

  const handleUpdatePaymentClick = async (payment) => {
    try {
      const paymentDetails = await getManualPaymentById(payment.id);
      setSelectedPayment(paymentDetails);
      setShowUpdateModal(true);
      setPartialAmount("");
      setError("");
    } catch (error) {
      toast.error("Failed to fetch payment details");
    }
  };

  const handleViewHistoryClick = async (payment) => {
    try {
      const paymentDetails = await getManualPaymentById(payment.id);
      setSelectedPayment(paymentDetails);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error("Failed to fetch payment history");
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 space-y-8 bg-background dark:bg-dark-background min-h-screen">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Payment Overview
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Track manual invoices and automated subscriptions.
          </p>
        </div>

        {/* Date Range Picker */}
        <div className="relative" ref={datePickerRef}>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-dark-muted shadow-sm hover:bg-accent dark:hover:bg-gray-800 transition-colors"
          >
            <Calendar size={16} />
            <span>
              {dateRange.start && dateRange.end
                ? `${dateRange.start} - ${dateRange.end}`
                : "Select Date Range"}
            </span>
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showDatePicker ? "rotate-180" : ""
              }`}
            />
          </button>

          {showDatePicker && (
            <div className="absolute right-0 mt-2 w-72 bg-card dark:bg-dark-surface rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border p-4 z-50 animate-in fade-in zoom-in-95 duration-200">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-1">
                    From
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm focus:outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-1">
                    To
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange((prev) => ({ ...prev, end: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm focus:outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => {
                      setDateRange({ start: "", end: "" });
                      setShowDatePicker(false);
                    }}
                    className="flex-1 py-2 text-sm font-medium text-gray-600 hover:bg-accent dark:text-dark-muted dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={applyDateFilter}
                    className="flex-1 py-2 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Tabs & Controls */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex bg-accent dark:bg-gray-800 p-1 rounded-xl">
            <button
              onClick={() => handleTabChange("Manual")}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === "Manual"
                  ? "bg-card dark:bg-dark-surface shadow-sm text-text dark:text-dark-text"
                  : "text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
              }`}
            >
              Manual Payments
            </button>
            <button
              onClick={() => handleTabChange("Subscription")}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === "Subscription"
                  ? "bg-card dark:bg-dark-surface shadow-sm text-text dark:text-dark-text"
                  : "text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
              }`}
            >
              Wage Payments
            </button>
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2.5 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl text-sm font-medium hover:bg-accent dark:hover:bg-gray-800 transition-colors shadow-sm text-text dark:text-dark-text"
            >
              <Download size={16} /> Export
            </button>
            {activeTab === "Manual" && (
              <button
                onClick={() => navigate("/invoices/new")}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none"
              >
                <Plus size={16} /> New Invoice
              </button>
            )}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search Bar */}
          <div className="flex-1 bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                size={16}
              />
              <input
                type="text"
                placeholder={
                  activeTab === "Manual"
                    ? "Search by name, phone, or ID..."
                    : "Search tx ref, client, caregiver…"
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text"
              />
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="flex items-center justify-center px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <Search size={16} />
            </button>

            {/* Status Filter Dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
              >
                <Filter size={16} />
                {activeTab === "Manual" ? statusFilter : wageStatusFilter}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${
                    showStatusDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {activeTab === "Manual"
                    ? statusOptions.map((status) => (
                        <button
                          key={status}
                          onClick={() => handleStatusFilter(status)}
                          className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            statusFilter === status
                              ? "bg-primary text-white dark:bg-primary"
                              : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                          }`}
                        >
                          {status}
                        </button>
                      ))
                    : wageStatusOptions.map((status) => (
                        <button
                          key={status}
                          onClick={() => {
                            setWageStatusFilter(status);
                            setShowStatusDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            wageStatusFilter === status
                              ? "bg-primary text-white dark:bg-primary"
                              : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Filter Buttons (Manual Only - Optional) */}
        {activeTab === "Manual" && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {["all", "overdue", "paid", "in progress", "not paid"].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => {
                    setSelectedStatus(status);
                    const statusMap = {
                      all: "All Invoices",
                      overdue: "Overdue",
                      paid: "Paid",
                      "in progress": "Pending",
                      "not paid": "Not paid",
                    };
                    setStatusFilter(statusMap[status]);
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors whitespace-nowrap ${
                    selectedStatus === status
                      ? "bg-primary text-white dark:bg-primary"
                      : "bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-800"
                  }`}
                >
                  {status === "all"
                    ? "All"
                    : status === "in progress"
                      ? "Pending"
                      : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ),
            )}
          </div>
        )}

        {/* Tables */}
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          {activeTab === "Manual" ? (
            <ManualPaymentsTable
              payments={paginatedManual}
              getStatusBadge={getStatusBadge}
              onWaivePenalty={handleWaivePenaltyClick}
              onViewDetails={handleViewDetailsClick}
              onUpdatePayment={handleUpdatePaymentClick}
              onViewHistory={handleViewHistoryClick}
              onViewNotes={handleViewNotesClick}
            />
          ) : (
            <WagePaymentsTable
              payments={paginatedSubs}
              getStatusBadge={getStatusBadge}
            />
          )}

          {/* Pagination */}
          {(paginatedManual.length > 0 || paginatedSubs.length > 0) && (
            <div className="border-t border-gray-100 dark:border-dark-border bg-accent/50 dark:bg-gray-800/30 p-2">
              <Pagination
                currentPage={currentPage}
                totalPages={
                  activeTab === "Manual" ? totalPagesManual : totalPagesSubs
                }
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Import Modal Components */}
      <UpdatePaymentModal
        selectedPayment={selectedPayment}
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        handleFullPayment={handleFullPayment}
        handlePartialPayment={handlePartialPayment}
        partialAmount={partialAmount}
        setPartialAmount={setPartialAmount}
        error={error}
        setError={setError}
      />

      <ViewDetailsModal
        selectedPayment={selectedPayment}
        showViewModal={showViewModal}
        setShowViewModal={setShowViewModal}
        getStatusBadge={getStatusBadge}
      />

      <WaivePenaltyModal
        selectedPayment={selectedPayment}
        showWaivePenaltyModal={showWaivePenaltyModal}
        setShowWaivePenaltyModal={setShowWaivePenaltyModal}
        handleWaivePenalty={handleWaivePenalty}
        penaltyNote={penaltyNote}
        setPenaltyNote={setPenaltyNote}
        error={error}
        setError={setError}
      />

      <HistoryModal
        selectedPayment={selectedPayment}
        showHistoryModal={showHistoryModal}
        setShowHistoryModal={setShowHistoryModal}
      />

      <NoteModal
        selectedPayment={selectedPayment}
        showNoteModal={showNoteModal}
        setShowNoteModal={setShowNoteModal}
      />
    </div>
  );
};

export default InvoicesList;
